import React, { useState, useEffect } from 'react';
import { Select, Radio, Button, Steps, Row, Col, Space, ConfigProvider, Typography, App, Tooltip, Form, message, Checkbox, Card, Upload } from 'antd';
import { motion } from 'framer-motion';
import SectionTitle from './text_components/SectionTitle';
import { InfoCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';
import axios from 'axios';
import { getCurrentUserId } from '../utils/userUtils';

const { Option } = Select;
const { Step } = Steps;
const { Title, Text } = Typography;

const StyledCard = styled(motion.div)`
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid #bfdbfe;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-5px);
  }
`;

const StyledButton = styled(Button)`
  border-radius: 10px;
  height: 40px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const ChecklistCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .ant-card-head {
    background: #f8fafc;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }
`;

const StyledSteps = styled(Steps)`
  .ant-steps-item-icon {
    background: white;
    border-color: #4a90e2;
  }

  .ant-steps-item-title {
    font-weight: 600;
  }
`;

const StyledUploadButton = styled(Button)`
  margin-left: 16px;
  font-size: 12px;
  height: 28px;
  padding: 0 12px;
  border-radius: 6px;
  
  &:hover {
    color: #4a90e2;
    border-color: #4a90e2;
  }
`;

const quarterlyChecklist = {
  'Plan Contributions': [
    'Verify receipt and accuracy of employer and employee contributions',
    'Ensure timely deposit of contributions into participant accounts'
  ],
  'Participant Census Updates': [
    'Update participant eligibility and employment status',
    'Process participant deferral changes'
  ],
  'Participant Loans and Distributions': [
    'Process loan requests, repayments, and hardship distributions',
    'Verify compliance with plan and IRS rules'
  ],
  'Compliance Monitoring': [
    'Deliver required participant notices',
    'Track completion of plan-specific compliance tasks'
  ],
  'Plan Document Management': [
    'Review and update plan document amendments as needed',
    'Share updated documents with sponsors and advisors'
  ],
  'Stakeholder Communication': [
    'Coordinate with plan sponsors to resolve data discrepancies',
    'Provide updates on completed TPA tasks to advisors'
  ],
  'Quarterly Reporting': [
    'Generate participant data updates report',
    'Document loan and distribution activity',
    'Summarize compliance progress'
  ]
};

const annualChecklist = {
  'Regulatory Filings': [
    'Prepare and file Form 5500',
    'Deliver required disclosures to participants',
    'Complete summary annual report distribution'
  ],
  'Nondiscrimination Testing': [
    'Conduct ADP/ACP testing',
    'Perform top-heavy testing',
    'Complete coverage testing',
    'Process any required corrective actions'
  ],
  'Participant Census and Data Validation': [
    'Prepare year-end participant census',
    'Validate compensation data',
    'Verify deferral amounts'
  ],
  'Plan Design Review': [
    'Review eligibility criteria',
    'Update contribution limits',
    'Verify vesting schedules',
    'Process plan amendments'
  ],
  'Document Review and Updates': [
    'Update adoption agreements',
    'Review and update SPDs',
    'Ensure all File Library are current'
  ],
  'Archive Management': [
    'Archive amendments and communications',
    'Organize and maintain digital records',
    'Update document retention system'
  ],
  'Audit Preparation': [
    'Compile and organize documentation for audits',
    'Address auditor questions and provide additional data',
    'Prepare audit support documentation'
  ],
  'Fee Disclosures': [
    'Assist sponsors in preparing and distributing annual fee disclosures',
    'Verify accuracy of fee structures with advisors',
    'Document fee disclosure distribution'
  ]
};

const steps = [
  {
    title: 'Select Type & Plan',
    content: ({ checklistType, handleChecklistTypeChange, selectedPlan, handlePlanChange, plans }) => (
      <Space direction="vertical" size="large" className="w-full">
        <div>
          <Text strong>Checklist Type</Text>
          <Radio.Group
            value={checklistType}
            onChange={handleChecklistTypeChange}
            className="ml-4"
          >
            <Radio.Button value="quarterly">Quarterly</Radio.Button>
            <Radio.Button value="annual">Annual</Radio.Button>
          </Radio.Group>
        </div>

        <div>
          <Text strong>Select Plan</Text>
          <Select
            placeholder="Choose a plan"
            style={{ width: '100%', marginTop: '8px' }}
            onChange={handlePlanChange}
            value={selectedPlan}
          >
            {plans.map(plan => (
              <Option key={plan.id} value={plan.id}>{plan.name}</Option>
            ))}
          </Select>
        </div>
      </Space>
    )
  },
  {
    title: 'Complete Checklist',
    content: ({ selectedPlan, calculateProgress, renderChecklist }) => (
      selectedPlan && (
        <>
          <div className="bg-blue-50 p-4 rounded-lg">
            <Text strong>
              Progress: {calculateProgress()}%
            </Text>
            <div
              className="bg-blue-200 h-2 rounded-full mt-2"
              style={{ width: `${calculateProgress()}%` }}
            />
          </div>

          <div className="mt-6">
            {renderChecklist()}
          </div>
        </>
      )
    )
  },
  {
    title: 'Review & Submit',
    content: ({ checklistType, selectedPlan, calculateProgress, completedTasks, taskDocuments }) => (
      <Space direction="vertical" size="large" className="w-full">
        <Title level={4}>Review Checklist Progress</Title>
        
        <div className="bg-blue-50 p-6 rounded-lg">
          <Space direction="vertical">
            <Text strong>Selected Plan: </Text>
            <Text>{selectedPlan}</Text>
            
            <Text strong className="mt-4">Checklist Type: </Text>
            <Text>{checklistType.charAt(0).toUpperCase() + checklistType.slice(1)}</Text>
            
            <Text strong className="mt-4">Overall Progress: </Text>
            <Text>{calculateProgress()}%</Text>
            
            <Text strong className="mt-4">Completed Tasks & Documents: </Text>
            <ul className="list-disc pl-6">
              {Object.entries(completedTasks)
                .filter(([_, isCompleted]) => isCompleted)
                .map(([taskKey]) => {
                  const [section, ...taskParts] = taskKey.split('-');
                  const task = taskParts.join('-');
                  const hasDocument = taskDocuments[taskKey];
                  return (
                    <li key={taskKey} className="text-green-600">
                      {task}
                      {hasDocument && (
                        <span className="ml-2 text-blue-500">
                          (Doc: {hasDocument.name})
                        </span>
                      )}
                    </li>
                  );
                })}
            </ul>
          </Space>
        </div>
      </Space>
    )
  }
];

const TPARequirements = () => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [checklistType, setChecklistType] = useState('quarterly');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [completedTasks, setCompletedTasks] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [taskDocuments, setTaskDocuments] = useState({});

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = () => {
    const mockPlans = [
      { id: '1', name: 'ABC Manufacturing 401(k) Plan' },
      { id: '2', name: 'XYZ Tech Solutions Retirement Plan' },
      { id: '3', name: 'Global Services Group 401(k)' },
      { id: '4', name: 'Innovative Systems Pension Plan' },
      { id: '5', name: 'First Choice Healthcare 403(b)' },
      { id: '6', name: 'Metro Transit Authority Plan' },
      { id: '7', name: 'Sunrise Educational Services 401(k)' },
      { id: '8', name: 'Pacific Northwest Industries Plan' }
    ];
    setPlans(mockPlans);
  };

  const handleChecklistTypeChange = (e) => {
    setChecklistType(e.target.value);
    setCompletedTasks({});
  };

  const handlePlanChange = (value) => {
    setSelectedPlan(value);
    setCompletedTasks({});
  };

  const toggleTask = (section, task) => {
    setCompletedTasks(prev => ({
      ...prev,
      [`${section}-${task}`]: !prev[`${section}-${task}`]
    }));
  };

  const calculateProgress = () => {
    const checklist = checklistType === 'quarterly' ? quarterlyChecklist : annualChecklist;
    let totalTasks = 0;
    let completedTasksCount = 0;

    Object.entries(checklist).forEach(([section, tasks]) => {
      tasks.forEach(task => {
        totalTasks++;
        if (completedTasks[`${section}-${task}`]) {
          completedTasksCount++;
        }
      });
    });

    return Math.round((completedTasksCount / totalTasks) * 100);
  };

  const handleDocumentUpload = (info, section, task) => {
    const { status, originFileObj } = info.file;
    
    if (status !== 'uploading') {
      setTaskDocuments(prev => ({
        ...prev,
        [`${section}-${task}`]: {
          file: originFileObj,
          name: info.file.name
        }
      }));
      messageApi.success(`${info.file.name} uploaded successfully`);
    }
    if (status === 'error') {
      messageApi.error(`${info.file.name} upload failed`);
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlan) {
      messageApi.error('Please select a plan');
      return;
    }

    setLoading(true);
    try {
      const progress = calculateProgress();
      const formData = new FormData();
      
      // Append basic data
      formData.append('userId', getCurrentUserId());
      formData.append('planId', selectedPlan);
      formData.append('checklistType', checklistType);
      formData.append('progress', progress);
      formData.append('completedTasks', JSON.stringify(completedTasks));

      // Append documents
      Object.entries(taskDocuments).forEach(([taskKey, docInfo]) => {
        if (docInfo && docInfo.file) {
          formData.append(`documents[${taskKey}]`, docInfo.file);
        }
      });

      await axios.post(`https://plansync-backend-234288438538.us-central1.run.app/api/checklist-completion`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      messageApi.success(`${checklistType.charAt(0).toUpperCase() + checklistType.slice(1)} checklist saved successfully!`);
    } catch (error) {
      messageApi.error('Failed to save checklist progress');
    } finally {
      setLoading(false);
    }
  };

  const renderChecklist = () => {
    const checklist = checklistType === 'quarterly' ? quarterlyChecklist : annualChecklist;

    return Object.entries(checklist).map(([section, tasks]) => (
      <ChecklistCard
        key={section}
        title={<Text strong>{section}</Text>}
        className="mb-4"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {tasks.map(task => (
            <div key={task} className="flex items-center justify-between">
              <div className="flex items-center flex-1">
                <Checkbox
                  checked={completedTasks[`${section}-${task}`]}
                  onChange={() => toggleTask(section, task)}
                >
                  {task}
                </Checkbox>
              </div>
              <div className="flex items-center ml-4">
                <Upload
                  maxCount={1}
                  onChange={(info) => handleDocumentUpload(info, section, task)}
                  showUploadList={false}
                >
                  <StyledUploadButton 
                    icon={<InfoCircleOutlined />}
                    size="small"
                  >
                    {taskDocuments[`${section}-${task}`] 
                      ? 'Update Doc' 
                      : 'Add Doc'
                    }
                  </StyledUploadButton>
                </Upload>
                {taskDocuments[`${section}-${task}`] && (
                  <Tooltip title={taskDocuments[`${section}-${task}`].name}>
                    <CheckCircleOutlined 
                      className="ml-2 text-green-500" 
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          ))}
        </Space>
      </ChecklistCard>
    ));
  };

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const next = () => {
    if (currentStep === 0 && !selectedPlan) {
      messageApi.error('Please select a plan before proceeding');
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  return (
    <App>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#4a90e2',
            borderRadius: 10,
            fontFamily: "'Inter', sans-serif",
          },
        }}
      >
        {contextHolder}
        <Row justify="center" align="middle" className="min-h-screen py-8">
          <Col xs={24} sm={24} md={22} lg={20} xl={18}>
            <StyledCard>
              <SectionTitle>TPA Requirements Checklist</SectionTitle>

              <StyledSteps current={currentStep}>
                {steps.map(item => (
                  <Step key={item.title} title={item.title} />
                ))}
              </StyledSteps>

              <div className="mt-8">
                {steps[currentStep].content({
                  checklistType,
                  handleChecklistTypeChange,
                  selectedPlan,
                  handlePlanChange,
                  plans,
                  calculateProgress,
                  renderChecklist,
                  completedTasks,
                  taskDocuments
                })}
              </div>

              <div className="steps-action mt-8">
                <Space>
                  {currentStep > 0 && (
                    <StyledButton onClick={prev}>
                      Previous
                    </StyledButton>
                  )}
                  {currentStep < steps.length - 1 && (
                    <StyledButton type="primary" onClick={next}>
                      Next
                    </StyledButton>
                  )}
                  {currentStep === steps.length - 1 && (
                    <StyledButton
                      type="primary"
                      onClick={handleSubmit}
                      loading={loading}
                      icon={<CheckCircleOutlined />}
                    >
                      Submit Checklist
                    </StyledButton>
                  )}
                </Space>
              </div>
            </StyledCard>
          </Col>
        </Row>
      </ConfigProvider>
    </App>
  );
};

export default TPARequirements;
